'use client';

import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import CmsImage from '@/components/ui/CmsImage';
import { stripHtml } from '@/lib/homepage-mappers';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import { getStrapiMediaUrl } from '@/lib/strapi-media-url';
import type { CommunityZigzagSection } from '@/types/community';
import { Reveal } from '../lib/community-reveal';

interface CommunityZigzagSectionViewProps {
  zone: CommunityZigzagSection;
  isEven: boolean;
}

export default function CommunityZigzagSectionView({
  zone,
  isEven,
}: CommunityZigzagSectionViewProps) {
  const stories = zone.item ?? [];
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const total = stories.length;
  if (total === 0) return null;

  const story = stories[index];
  const imageUrl = getStrapiMediaUrl(story.media, 'large');
  const sectionId = zone.sectionId?.replace(/^#/, '') || `section-${zone.id}`;
  const title = story.heading?.trim() || '';
  const body = story.description ? stripHtml(story.description) : '';

  const go = (dir: 1 | -1) => {
    const next = index + dir;
    if (next < 0 || next >= total) return;
    setDirection(dir);
    setIndex(next);
  };

  const renderPaginationFooter = () => {
    if (total <= 1) return null;
    return (
      <div className="mt-10 flex flex-col gap-5 border-t border-stone-100 pt-6 sm:mt-14 sm:flex-row sm:items-center sm:justify-between sm:pt-8">
        <div className="flex items-center gap-2.5">
          {stories.map((s, i) => (
            <button
              key={s.id}
              type="button"
              onClick={() => {
                setDirection(i > index ? 1 : -1);
                setIndex(i);
              }}
              className="group flex flex-col gap-1.5"
              aria-label={`Story ${i + 1}: ${s.heading ?? ''}`}
            >
              <div
                className="rounded-full transition-all duration-300"
                style={{
                  width: i === index ? '2.2rem' : '0.45rem',
                  height: '0.45rem',
                  backgroundColor: i === index ? '#ed1c22' : '#d6d3d1',
                }}
              />
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={() => go(-1)}
            disabled={index === 0}
            className="group flex h-12 w-12 items-center justify-center rounded-full border border-stone-200 bg-white transition-all duration-200 hover:border-stone-300 hover:shadow-sm disabled:cursor-not-allowed disabled:opacity-20"
            aria-label="Previous story"
          >
            <svg
              viewBox="0 0 20 20"
              fill="currentColor"
              className="h-4 w-4 text-stone-400 transition-colors group-hover:text-stone-700"
            >
              <path
                fillRule="evenodd"
                d="M17 10a.75.75 0 01-.75.75H5.612l4.158 3.96a.75.75 0 11-1.04 1.08l-5.5-5.25a.75.75 0 010-1.08l5.5-5.25a.75.75 0 111.04 1.08L5.612 9.25H16.25A.75.75 0 0117 10z"
                clipRule="evenodd"
              />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => go(1)}
            disabled={index === total - 1}
            className="flex h-12 w-12 items-center justify-center rounded-full bg-[#ed1c22] transition-all duration-200 hover:scale-105 hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-20"
            style={{
              boxShadow: index < total - 1 ? '0 6px 20px -4px rgba(237,28,34,0.5)' : 'none',
            }}
            aria-label="Next story"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 text-white">
              <path
                fillRule="evenodd"
                d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      </div>
    );
  };

  return (
    <section
      id={sectionId}
      className="relative scroll-mt-[var(--site-header-height,80px)] overflow-hidden"
      style={{ backgroundColor: isEven ? '#f7f4f1' : '#fafaf9' }}
      aria-labelledby={`${sectionId}-heading`}
    >
      <div className="container mx-auto max-sm:px-4">
        {zone.heading ? (
          <Reveal direction="up" delay={0}>
            <div className="mb-10 sm:mb-12">
              <Reveal direction="up" delay={160}>
                <h2
                  id={`${sectionId}-heading`}
                  className="text-[1.75rem] font-bold leading-[1.2] tracking-tight text-[#1a1a1a] sm:text-4xl sm:leading-[1.15] md:text-[2.75rem] lg:text-5xl"
                >
                  {zone.heading}
                </h2>
              </Reveal>

              <div className="mt-6 flex items-center gap-3">
                <motion.div
                  className="h-px flex-1 bg-stone-200"
                  initial={{ scaleX: 0, originX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.9, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
                />
                <motion.div
                  className="h-px w-8 bg-stone-200"
                  initial={{ scaleX: 0, originX: 1 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
                />
              </div>
            </div>
          </Reveal>
        ) : null}

        <div
          className={`flex flex-col gap-10 lg:flex-row lg:items-stretch lg:gap-14 xl:gap-20 ${!isEven ? 'lg:flex-row-reverse' : ''}`}
        >
          <Reveal direction={isEven ? 'left' : 'right'} delay={200} className="w-full lg:w-[52%]">
            <div className="relative h-[52vw] min-h-[260px] overflow-hidden rounded-2xl shadow-[0_32px_80px_-16px_rgba(0,0,0,0.2)] sm:h-[50vw] sm:min-h-[300px] sm:rounded-[2rem] md:min-h-[340px] lg:h-full lg:min-h-[540px] lg:rounded-[2.5rem]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={`img-${index}`}
                  className="absolute inset-0"
                  initial={{ opacity: 0, scale: 1.07 }}
                  animate={{
                    opacity: 1,
                    scale: 1,
                    transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1] },
                  }}
                  exit={{ opacity: 0, transition: { duration: 0.3 } }}
                >
                  <CmsImage
                    src={imageUrl}
                    alt={story.media?.alternativeText || title}
                    fill
                    className="object-cover"
                    sizes="(max-width: 1024px) 100vw, 52vw"
                    priority={index === 0}
                    fallbackSrc={MEDIA_PLACEHOLDER}
                  />
                </motion.div>
              </AnimatePresence>

              <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#ed1c22]" />

              <div className="absolute bottom-0 left-0 right-0 p-7 sm:p-8">
                <AnimatePresence mode="wait">
                  <motion.p
                    key={`caption-${index}`}
                    className="text-xs font-semibold uppercase tracking-[0.22em] text-white/70"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0, transition: { duration: 0.5, delay: 0.2 } }}
                    exit={{ opacity: 0 }}
                  >
                    {title}
                  </motion.p>
                </AnimatePresence>
              </div>

              {total > 1 ? (
                <div className="absolute right-6 top-6 flex items-center gap-1 rounded-full bg-black/30 px-3 py-1.5 backdrop-blur-sm">
                  <span className="text-sm font-bold text-white tabular-nums">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className="text-white/40">/</span>
                  <span className="text-xs font-semibold text-white/50 tabular-nums">
                    {String(total).padStart(2, '0')}
                  </span>
                </div>
              ) : null}
            </div>
          </Reveal>

          <Reveal
            direction={isEven ? 'right' : 'left'}
            delay={320}
            className="flex w-full flex-col justify-between lg:w-[48%] lg:py-4"
          >
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={`text-${index}`}
                custom={direction}
                initial={{ opacity: 0, y: direction > 0 ? 30 : -30 }}
                animate={{
                  opacity: 1,
                  y: 0,
                  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
                }}
                exit={{ opacity: 0, y: direction > 0 ? -20 : 20, transition: { duration: 0.22 } }}
                className="flex-1"
              >
                {title ? (
                  <h3 className="text-2xl font-bold leading-tight tracking-tight text-[#1a1a1a] sm:text-3xl lg:text-4xl">
                    {title}
                  </h3>
                ) : null}
                <div className="mt-5 h-[3px] w-14 rounded-full bg-[#ed1c22]" />
                {body ? (
                  <p className="mt-7 text-sm leading-7 text-stone-600 sm:text-[15px] sm:leading-8">
                    {body}
                  </p>
                ) : null}
              </motion.div>
            </AnimatePresence>

            {renderPaginationFooter()}
          </Reveal>
        </div>
      </div>
    </section>
  );
}
